import { Component } from 'react';

import { RouteProps } from '../../types/route-types';
import styles from './growth-pages.module.scss';

/**
 * Designed replacement for the markdown-rendered /how-to-help-xconvert-to-grow.
 *
 * CONTENT CONTRACT: the route's SSR head ships FAQPage + HowTo JSON-LD generated
 * from sources/md/xconvert/how-to-help-xconvert-to-grow.md. Every FAQ question
 * and every HowTo step in that markdown must stay VISIBLE on this page, with
 * faithful answers — schema describing content a page doesn't show is a spam
 * signal. If the md changes, mirror it here (and vice versa).
 */
export default class HelpUsGrowComponent extends Component<RouteProps> {

    render() {
        return <div className={styles.page}>
            <div className={styles.hero}>
                <h1>XConvert is free.<br />You can keep it that way.</h1>
                <p>
                    Every file you convert or compress is processed on real servers — that
                    costs real money, paid for by ads and a few subscribers, never by
                    watermarks or accounts. The most useful thing a happy user can do is
                    help the next person find us. Most of what follows takes two minutes,
                    none of it costs anything, and none of it is an obligation.
                </p>
                <div className={styles.heroCtas}>
                    <a className={styles.ctaPrimary} href="/link-us">🔗 Link to us — the one that compounds</a>
                    <a className={styles.ctaSecondary} href="https://www.trustpilot.com/evaluate/xconvert.com"
                        target="_blank" rel="noopener">⭐ Leave a review</a>
                </div>
            </div>

            {/* The four HowTo steps — same four as the markdown/schema. */}
            <h2 className={styles.sectionTitle}>How to Help in Five Minutes or Less</h2>
            <p className={styles.sectionSub}>Four ways, honestly ranked. The highlighted one matters most.</p>
            <div className={styles.cardGrid}>
                <div className={`${styles.card} ${styles.cardBest}`}>
                    <span className={styles.bestBadge}>BIGGEST IMPACT</span>
                    <span className={styles.cardEmoji}>🔗</span>
                    <h3>Link to us from a page you own</h3>
                    <p>
                        A blog, documentation site, wiki, class page or company intranet — one
                        plain contextual link is the only item on this list that compounds.
                        Search engines treat an editorial link from a relevant page as a real
                        signal, and it keeps sending people for years.
                    </p>
                    <a href="/link-us">Get a ready-made snippet →</a>
                    <span className={styles.cardMeta}>~5 minutes · keeps working for years</span>
                </div>
                <div className={styles.card}>
                    <span className={styles.cardEmoji}>⭐</span>
                    <h3>Leave an honest review</h3>
                    <p>
                        Rate us on Trustpilot or Product Hunt. One sentence naming the actual
                        job you ran — &ldquo;compressed a 700 MB MP4 for email&rdquo; — is worth more to
                        the next visitor than any star count. Reviews are what people check
                        before trusting a site with their file.
                    </p>
                    <a href="https://www.trustpilot.com/evaluate/xconvert.com" target="_blank" rel="noopener">Review on Trustpilot →</a>
                    <span className={styles.cardMeta}>~2 minutes · builds trust</span>
                </div>
                <div className={styles.card}>
                    <span className={styles.cardEmoji}>📤</span>
                    <h3>Share the exact tool you used</h3>
                    <p>
                        Send someone the specific page — Compress JPEG, Video Compressor — not
                        the homepage. A deep link drops them straight onto the working tool
                        instead of a menu they have to navigate.
                    </p>
                    <a href="/downloads">Share from your downloads →</a>
                    <span className={styles.cardMeta}>~30 seconds · direct referral</span>
                </div>
                <div className={styles.card}>
                    <span className={styles.cardEmoji}>🐛</span>
                    <h3>Tell us what broke</h3>
                    <p>
                        A precise bug report is worth as much as a share: format pair, the
                        options you picked, expected vs. actual, and the file itself if it
                        isn&apos;t sensitive. It fixes the tool for everyone who hits the same file.
                    </p>
                    <a href="/contact-us">Report a bug →</a>
                    <span className={styles.cardMeta}>~5 minutes · fixes it for everyone</span>
                </div>
            </div>

            <h2 className={styles.sectionTitle}>What Each Kind of Help Actually Does</h2>
            <p className={styles.sectionSub}>
                Not all help is equal, and it would be dishonest to pretend otherwise. A tweet
                disappears in a day; a link on a page that ranks stays useful for years.
            </p>
            <div className={styles.tableWrap}>
                <table className={styles.impactTable}>
                    <thead>
                        <tr><th>Way to help</th><th>Time</th><th>What it actually changes</th></tr>
                    </thead>
                    <tbody>
                        <tr><td>Contextual link from your site</td><td>~5 min</td>
                            <td>The only one that compounds — an editorial link from a relevant page is a genuine ranking signal and keeps referring people for years.</td></tr>
                        <tr><td>Trustpilot / Product Hunt review</td><td>~2 min</td>
                            <td>Gives a first-time visitor a reason to trust an unfamiliar site with their file — the thing people check before uploading anything.</td></tr>
                        <tr><td>Deep link to the tool you used</td><td>~30 sec</td>
                            <td>Direct referral with the highest conversion here — the person arrives with the job half done.</td></tr>
                        <tr><td>Answering a &ldquo;how do I convert X to Y&rdquo; question</td><td>~5 min</td>
                            <td>Long tail — the thread stays indexed and keeps surfacing. Only worth doing where the tool genuinely answers the question.</td></tr>
                        <tr><td>A detailed bug report</td><td>~5 min</td>
                            <td>Concretely more useful than a share if something is actually wrong.</td></tr>
                        <tr><td>Mentioning us in a tutorial or roundup</td><td>30+ min</td>
                            <td>Highest impact, highest effort. Works best as one method among several, not as an advert.</td></tr>
                        <tr><td>A paid Basic or Pro plan</td><td>~1 min</td>
                            <td>Direct funding, and it removes ads for you. Entirely optional — see <a href="/product">Pricing</a>.</td></tr>
                    </tbody>
                </table>
            </div>

            <h2 className={styles.sectionTitle}>Where a Link or Mention Genuinely Helps</h2>
            <p className={styles.sectionSub}>
                Most platforms deliberately strip the search-ranking value out of outbound
                links. That doesn&apos;t make them worthless — a human still clicks — but it
                changes where a link is worth placing.
            </p>
            <div className={styles.tableWrap}>
                <table className={styles.impactTable}>
                    <thead>
                        <tr><th>Where</th><th>What works there</th><th>Passes ranking value?</th></tr>
                    </thead>
                    <tbody>
                        <tr><td>Your own blog, docs or wiki</td>
                            <td>A plain in-context link to the specific tool, inside a sentence that explains why</td>
                            <td>✅ Yes — a normal followed link</td></tr>
                        <tr><td>Reddit, Quora, Stack Exchange, Discord</td>
                            <td>Answer the actual question first; mention the tool only if it fits</td>
                            <td>Usually not — outbound links are tagged <code>nofollow</code>/<code>ugc</code>. The value is the humans who click</td></tr>
                        <tr><td>X, LinkedIn, Facebook</td>
                            <td>A short post naming the specific job you did</td>
                            <td>No — same treatment. The value is reach</td></tr>
                        <tr><td>&ldquo;Best free tools&rdquo; roundups</td>
                            <td>A one-line entry linking a specific tool page</td>
                            <td>Usually yes, unless the list is marked sponsored</td></tr>
                        <tr><td>Course notes, syllabi, team wikis</td>
                            <td>A link next to the assignment or workflow that needs it</td>
                            <td>Only if publicly crawlable — but the referral value is real either way</td></tr>
                    </tbody>
                </table>
            </div>

            <div className={styles.honest}>
                A request, not a rule: please don&apos;t mass-post, don&apos;t add us to Wikipedia, and
                don&apos;t drop a link into a thread the tool doesn&apos;t actually answer. Spammed
                mentions get removed, they annoy the community that removes them, and they do
                us more harm than the link is worth. One genuinely relevant answer beats
                twenty drive-by ones.
            </div>

            <h2 className={styles.sectionTitle}>Frequently Asked Questions</h2>
            <div className={styles.faq}>
                <details>
                    <summary>Does leaving a review actually do anything?</summary>
                    <p>Yes, and more than sharing does. The main obstacle for any online converter is
                        that a first-time visitor is about to hand a file to a website they have never
                        heard of, and independent reviews are what they check first. A specific review —
                        which tool, what the file was, whether the output was right — is far more
                        persuasive than five stars with no text. It also tells us which tools people
                        actually rely on.</p>
                </details>
                <details>
                    <summary>Is XConvert really free — what is the catch?</summary>
                    <p>The catch is ads. The free tier shows advertising, and that pays for the servers
                        that do the encoding. No trial that expires, no per-file cap that turns into a
                        paywall, no watermark, no account required. The optional Basic and Pro plans
                        remove ads and raise limits, and there is a separately metered Developer API.
                        That is the whole business model.</p>
                </details>
                <details>
                    <summary>Can I use XConvert at work or in my school?</summary>
                    <p>There is no account, seat licence or per-user gate, so nothing in the product
                        stops you. Two caveats worth taking seriously: files are uploaded to our servers
                        over an encrypted connection and processed there, so if your employer or
                        institution restricts sending documents to third-party services, check that
                        policy first — that applies to every online converter. And the licence terms for
                        our own site content and branding are in the <a href="/terms-of-service">Terms of Service</a>.
                        For regulated or client-owned material, a tool your organisation hosts itself is
                        the safer answer, and we would rather say so than pretend otherwise.</p>
                </details>
                <details>
                    <summary>Should I link to the homepage or to a specific tool page?</summary>
                    <p>The specific tool page, almost always. Someone who lands on the homepage has to
                        work out which of several thousand tools they wanted; someone who lands on
                        <a href="/compress-jpeg"> Compress JPEG</a> is one drag-and-drop from being finished.
                        Good targets are the job-shaped pages — <a href="/video-compressor">Video Compressor</a>,{' '}
                        <a href="/compress-mp3">Compress MP3</a>, <a href="/merge-image-to-pdf">Merge Images to PDF</a> —
                        or a format hub such as <a href="/image-converter">Image Converter</a>,{' '}
                        <a href="/video-converter">Video Converter</a> or <a href="/audio-converter">Audio Converter</a>.</p>
                </details>
                <details>
                    <summary>Do links from Reddit, Quora, or social media still help your search ranking?</summary>
                    <p>Mostly not, and it is better to know that up front. Those platforms tag outbound
                        links with <code>nofollow</code> or <code>ugc</code>, which search engines generally do not
                        follow. What they do carry is people: a good answer in a thread that keeps
                        ranking for &ldquo;how do I convert X to Y&rdquo; can send readers for years. So post
                        where it genuinely helps someone, and do not treat it as an SEO exercise.</p>
                </details>
                <details>
                    <summary>How do I report a conversion that came out wrong?</summary>
                    <p>Send it through <a href="/contact-us">Contact Us</a> with four things: the source
                        format, the target format, any options you changed, and what was wrong with the
                        output. Attach the input file if you can — format bugs are usually specific to
                        how one encoder wrote one file. Add your email if you want an answer; the field
                        is optional so that people who just want to flag something can.</p>
                </details>
                <details>
                    <summary>Can I request a format or a tool that is not here yet?</summary>
                    <p>Yes, and specific requests are the useful kind. Tell us the extension, what
                        created the file, and what you need to open the result in — &ldquo;convert to MP4&rdquo;
                        and &ldquo;convert to something my 2014 TV will play&rdquo; lead to different defaults.
                        Obscure requests are welcome; a format nobody else converts is exactly the kind
                        of gap worth filling.</p>
                </details>
                <details>
                    <summary>Can I write about XConvert or use screenshots without asking?</summary>
                    <p>Yes. You never need permission to review us, write a tutorial that uses a tool
                        as a step, include screenshots, or put us in a comparison — including an
                        unfavourable one. If you want the logo as a banner, it is on
                        <a href="/link-us"> Link to Us</a> along with ready-made HTML. The only thing we
                        ask is that the link points at the tool page you actually wrote about.</p>
                </details>
                <details>
                    <summary>What happens to my file after I convert it?</summary>
                    <p>It is uploaded over an encrypted connection and processed on our servers. An
                        automated cleanup job then deletes files older than a few hours; in rare cases a
                        file can sit slightly longer before the sweep picks it up. Files are not shared
                        or made public. Full detail is in the <a href="/privacy-policy">Privacy Policy</a>,
                        with more answers on the <a href="/faq">FAQ</a> page.</p>
                </details>
            </div>

            <div className={styles.footNote}>
                Would rather support us with money? The free tier is not going away, but the
                ad-free <a href="/product">Basic and Pro plans</a> carry higher limits, and there is a
                metered <a href="/product">Developer API</a> for conversion inside your own software.
            </div>
        </div>;
    }
}
